import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const AGENT_API_URL = process.env.REACT_APP_AGENT_API_URL || 'https://back.bullxch.com';
const AGENT_API_KEY = process.env.REACT_APP_AGENT_API_KEY;

// Create separate axios instance for agent API
const agentApi = axios.create({
  baseURL: AGENT_API_URL,
  headers: {
    'Content-Type': 'application/json',
    ...(AGENT_API_KEY ? { 'X-API-Key': AGENT_API_KEY } : {})
  }
});

// Add req/res interceptors to handle token
let authContext: ReturnType<typeof useAuth> | null = null;

// Function to set auth context from components
export const setAuthContext = (context: ReturnType<typeof useAuth>) => {
  authContext = context;
};

// Request interceptor to add auth token
agentApi.interceptors.request.use(
  async (config) => {
    // If we have auth context and we're authenticated, add the token
    if (authContext && authContext.isAuthenticated) {
      const token = localStorage.getItem('privy_token');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle token expiration
agentApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If we have an authorization error and haven't tried to refresh the token yet
    if (error.response && 
        (error.response.status === 401 || error.response.status === 403) && 
        error.response.data && 
        (error.response.data.error.includes('expired') || 
         error.response.data.error.includes('invalid token')) && 
        !originalRequest._retry) {
      
      // Mark that we're retrying this request
      originalRequest._retry = true;
      
      // Try to refresh the token
      if (authContext) {
        try {
          const newToken = await authContext.refreshAuthToken();
          
          if (newToken) {
            // Update the request with the new token
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            // Retry the request
            return agentApi(originalRequest);
          }
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
        }
      }
    }
    
    return Promise.reject(error);
  }
);

interface AgentMessage {
  message: string;
  session_id?: string;
  user_id: string;
  context?: Record<string, any>;
}

interface AgentResponse {
  session_id: string;
  message: string;
  intent: string;
  needs_more_info: boolean;
  follow_up_question?: string;
  action_required: boolean;
  action_type: string;
  action_details: Record<string, any>;
  conversation_id?: string;
}

// Define action types as literals for type safety
type BlockchainActionType = 'check_balance' | 'execute_transaction' | 'verify_authorization';

// Define transaction types for execute_transaction
type TransactionType = 'send' | 'buy' | 'sell';

// Specific details interfaces for different action types
interface CheckBalanceDetails {
  sender: string;
  token?: string;
}

interface SendTransactionDetails {
  transaction_type: 'send';
  sender: string;
  recipient: string;
  amount: string;
  token?: string;
}

interface BuyTransactionDetails {
  transaction_type: 'buy';
  sender: string;
  token_address: string;
  amount: string;
}

interface SellTransactionDetails {
  transaction_type: 'sell';
  sender: string;
  token_address: string;
  amount?: string;
  percentage?: string;
}

interface VerifyAuthorizationDetails {
  username: string;
}

// Union type of all possible action details
type BlockchainActionDetails = 
  | CheckBalanceDetails 
  | SendTransactionDetails 
  | BuyTransactionDetails 
  | SellTransactionDetails
  | VerifyAuthorizationDetails;

interface BlockchainActionRequest {
  action_type: BlockchainActionType;
  action_details: BlockchainActionDetails;
  user_id: string;
  session_id?: string;
  conversation_id: string;
  tweet_id: string;
}

interface BlockchainActionResponse {
  success: boolean;
  message: string;
  status?: 'success' | 'failed';
  tx_hash?: string;
  details?: Record<string, any>;
  error?: string;
  timestamp?: string;
}

class AgentService {
  private currentSessionId: string | null = null;
  private sessionActive = false;
  private conversationId: string = '';

  /**
   * Sends a message to the agent API
   * @param message The user's message
   * @param userId The Twitter handle of the user from Privy
   * @returns The agent's response
   */
  async sendMessage(message: string, userId: string): Promise<AgentResponse> {
    try {
      const payload: AgentMessage = {
        message,
        user_id: userId,
      };

      // If we have an active session, include the session ID
      if (this.sessionActive && this.currentSessionId) {
        payload.session_id = this.currentSessionId;
      }
      
      // If we have a conversation ID, include it
      if (this.conversationId) {
        payload.context = {
          ...(payload.context || {}),
          conversation_id: this.conversationId
        };
      }

      const response = await agentApi.post<AgentResponse>('/api/chat', payload);
      
      // Update session information
      this.currentSessionId = response.data.session_id;
      this.sessionActive = response.data.needs_more_info;
      
      // Store conversation ID if it's in the response
      if (response.data.conversation_id) {
        this.conversationId = response.data.conversation_id;
      }

      // If conversation is complete (needs_more_info = false),
      // end the session for next conversation
      if (!response.data.needs_more_info) {
        this.sessionActive = false;
        this.currentSessionId = null;
        this.conversationId = '';
      }

      return response.data;
    } catch (error) {
      console.error('Error sending message to agent:', error);
      throw error;
    }
  }

  /**
   * Executes a blockchain action via the agent API
   * @param actionType The type of blockchain action to perform
   * @param actionDetails Additional details required for the action
   * @param userId The user ID
   * @param sessionId Optional session ID
   * @returns The result of the blockchain action
   */
  async executeBlockchainAction(
    actionType: string,
    actionDetails: Record<string, any>,
    userId: string,
    sessionId?: string
  ): Promise<BlockchainActionResponse> {
    try {
      // Validate action type
      if (!['check_balance', 'execute_transaction', 'verify_authorization'].includes(actionType)) {
        return {
          success: false,
          status: 'failed',
          message: `Unsupported action type: ${actionType}`,
          error: 'INVALID_ACTION_TYPE'
        };
      }

      // Generate a conversation ID if we don't have one
      if (!this.conversationId) {
        this.conversationId = `conv_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      }

      // Basic validation based on action type
      if (actionType === 'check_balance' && !actionDetails.sender) {
        return {
          success: false,
          status: 'failed',
          message: 'Missing required parameter: sender',
          error: 'MISSING_PARAMETER'
        };
      }

      if (actionType === 'execute_transaction') {
        // Validate transaction type
        if (!actionDetails.transaction_type || 
            !['send', 'buy', 'sell'].includes(actionDetails.transaction_type)) {
          return {
            success: false,
            status: 'failed',
            message: `Invalid or missing transaction_type. Must be one of: send, buy, sell`,
            error: 'INVALID_TRANSACTION_TYPE'
          };
        }

        // Validate transaction-specific parameters
        if (actionDetails.transaction_type === 'send' && 
            (!actionDetails.sender || !actionDetails.recipient || !actionDetails.amount)) {
          return {
            success: false,
            status: 'failed',
            message: 'Missing required parameters for send transaction',
            error: 'MISSING_PARAMETERS'
          };
        }

        if (actionDetails.transaction_type === 'buy' &&
            (!actionDetails.sender || !actionDetails.token_address || !actionDetails.amount)) {
          return {
            success: false,
            status: 'failed',
            message: 'Missing required parameters for buy transaction',
            error: 'MISSING_PARAMETERS'
          };
        }

        if (actionDetails.transaction_type === 'sell' &&
            (!actionDetails.sender || !actionDetails.token_address || 
             (!actionDetails.amount && !actionDetails.percentage))) {
          return {
            success: false,
            status: 'failed',
            message: 'Missing required parameters for sell transaction',
            error: 'MISSING_PARAMETERS'
          };
        }
      }

      const payload: BlockchainActionRequest = {
        action_type: actionType as BlockchainActionType,
        action_details: actionDetails as BlockchainActionDetails,
        user_id: userId,
        session_id: sessionId || this.currentSessionId || undefined,
        conversation_id: this.conversationId,
        tweet_id: actionDetails.tweet_id || `web_${Date.now()}`
      };

      console.log('Executing blockchain action:', payload);

      const response = await agentApi.post<BlockchainActionResponse>(
        '/api/blockchain-actions',
        payload
      );

      return {
        ...response.data,
        success: response.data.status === 'success'
      };
    } catch (error) {
      console.error('Error executing blockchain action:', error);
      
      // Handle specific error types
      if (axios.isAxiosError(error)) {
        if (error.response) {
          // Server responded with an error
          const statusCode = error.response.status;
          const responseData = error.response.data as any;
          
          // Check for registration error message
          if (responseData?.error && 
              (responseData.error.includes('register') || 
               responseData.error.includes('connection'))) {
            return {
              success: false,
              status: 'failed',
              message: responseData.error,
              error: 'REGISTRATION_REQUIRED'
            };
          }
          
          // Check for token expiration errors
          if (responseData?.error && 
              (responseData.error.includes('expired') || 
               responseData.error.includes('token') || 
               responseData.error.includes('JWT')) &&
              (responseData.error.includes('auth') || 
               responseData.error.includes('authentication'))) {
            return {
              success: false,
              status: 'failed',
              message: 'Your authentication token has expired',
              error: 'TOKEN_EXPIRED'
            };
          }
          
          // Check for insufficient balance error
          if (responseData?.error && responseData.error.includes('Insufficient balance')) {
            return {
              success: false,
              status: 'failed',
              message: responseData.error,
              error: 'INSUFFICIENT_BALANCE'
            };
          }
          
          if (statusCode === 401 || statusCode === 403) {
            return {
              success: false,
              status: 'failed',
              message: 'Unauthorized: Please connect and authorize your wallet first',
              error: 'UNAUTHORIZED'
            };
          }
          
          if (statusCode === 400) {
            return {
              success: false,
              status: 'failed',
              message: responseData?.error || 'Invalid request parameters',
              error: responseData?.error_code || 'BAD_REQUEST'
            };
          }
          
          if (statusCode === 500) {
            return {
              success: false,
              status: 'failed',
              message: 'Blockchain transaction failed. Please try again later.',
              error: 'TRANSACTION_FAILED'
            };
          }
          
          return {
            success: false,
            status: 'failed',
            message: responseData?.error || `Action failed: ${error.message}`,
            error: responseData?.error_code || 'API_ERROR'
          };
        }
        
        // Network error
        return {
          success: false,
          status: 'failed',
          message: 'Failed to connect to the blockchain service. Please check your internet connection.',
          error: 'NETWORK_ERROR'
        };
      }
      
      // Generic error
      return {
        success: false,
        status: 'failed',
        message: error instanceof Error ? error.message : 'Unknown error occurred',
        error: 'UNKNOWN_ERROR'
      };
    }
  }

  /**
   * Explicitly ends the current session
   */
  endSession(): void {
    this.sessionActive = false;
    this.currentSessionId = null;
  }

  /**
   * Checks if a session is currently active
   */
  isSessionActive(): boolean {
    return this.sessionActive;
  }

  /**
   * Gets the current session ID
   */
  getCurrentSessionId(): string | null {
    return this.currentSessionId;
  }

  /**
   * Updates the conversation ID for the current session
   * @param conversationId The new conversation ID
   */
  setConversationId(conversationId: string): void {
    this.conversationId = conversationId;
  }

  /**
   * Gets the current conversation ID
   */
  getConversationId(): string {
    return this.conversationId;
  }
}

// Export as singleton
export default new AgentService(); 